/**
 * ComponentPropTransformer.js
 * 
 * Utility functions for standardizing component props across the application.
 * This ensures a consistent data structure for all components, making the app
 * more robust against API changes and ensuring proper data persistence.
 */

/**
 * Standardizes component props to ensure a consistent structure across all components
 * 
 * @param {Object} data - The raw component data from API or local state
 * @param {String} componentType - The type of component (optional)
 * @returns {Object} - Standardized component data
 */
export const standardizeComponentProps = (data, componentType = null) => {
  if (!data) return {};
  
  // Ensure component_name is preserved
  const component_name = data.component_name || componentType || 'Unknown';
  
  // Create a base standardized object with common fields
  const standardized = {
    ...data,
    component_name,
    title: data.title || data.heading || data.header || "",
    description: data.description || data.desc || data.content || data.text || "",
  };

  // Handle image fields with various possible names
  standardized.image = data.image || data.img || data.image_url || data.banner_image || data.logo || null;
  // Ensure image placeholder is preserved if it exists
  if (data.image_placeholder && !standardized.image) {
    standardized.image_placeholder = data.image_placeholder;
  }
  
  // Ensure features array exists
  standardized.features = data.features || data.buttons || data.ctas || [];
  
  // Ensure links array exists for components that need it
  standardized.links = data.links || data.nav_links || [];
  
  // Component-specific standardization
  switch (component_name) {
    case 'Header':
      // Additional Header-specific properties
      standardized.logo = standardized.image;
      break;
      
    case 'Footer':
      // Footer-specific properties
      standardized.copyright = data.copyright || `© ${new Date().getFullYear()} All rights reserved`;
      break;
      
    case 'Banner':
      // Banner-specific properties
      standardized.overlay = data.overlay || false;
      standardized.overlay_opacity = data.overlay_opacity || 0.5;
      standardized.overlay_color = data.overlay_color || "#000000";
      standardized.text_position = data.text_position || "below";
      standardized.height = data.height || "300px";
      break;
      
    case 'Button':
      // Button-specific properties
      standardized.link = data.link || data.url || data.href || "#";
      standardized.button_text = data.button_text || data.text || data.title || "Learn More";
      standardized.button_color = data.button_color || "#007bff";
      standardized.text_color = data.text_color || "#ffffff";
      break;
      
    // Add more component-specific standardization as needed
  }
  
  return standardized;
};

/**
 * Transforms component array from API response to a structured object for EmailDetailsContainer
 * 
 * @param {Array} components - Array of components from API
 * @returns {Object} - Object with component IDs as keys and standardized component data as values
 */
export const transformApiComponentsToStructured = (components) => {
  if (!Array.isArray(components)) return {};
  
  return components.reduce((result, component, index) => {
    const id = component.id || `section-${Date.now()}-${index}`;
    result[id] = standardizeComponentProps(component);
    return result;
  }, {});
};

/**
 * Helper to safely extract a specific field from component data
 * 
 * @param {Object} data - Component data
 * @param {Array} fieldNames - Possible field names to check in order of preference
 * @param {*} defaultValue - Default value if no matching field is found
 * @returns {*} - The field value or default value
 */
export const extractField = (data, fieldNames, defaultValue = null) => {
  if (!data) return defaultValue;
  
  for (const field of fieldNames) {
    if (data[field] !== undefined) {
      return data[field];
    }
  }
  
  return defaultValue;
};

/**
 * Checks if a component has all required properties and adds defaults if missing
 * 
 * @param {Object} component - The component data to validate
 * @param {Object} requiredProps - Object with property names and default values
 * @returns {Object} - Validated component with all required properties
 */
export const validateComponentProps = (component, requiredProps = {}) => {
  if (!component) return { ...requiredProps };
  
  const validated = { ...component };
  
  // Add any missing required properties with default values
  Object.entries(requiredProps).forEach(([key, defaultValue]) => {
    if (validated[key] === undefined) {
      validated[key] = defaultValue;
    }
  });
  
  return validated;
};

export default {
  standardizeComponentProps,
  transformApiComponentsToStructured,
  extractField,
  validateComponentProps
}; 